import './styles.scss';

import { useEffect, useRef, useCallback, useState } from 'react';

import Header from '../../components/header';

import Pricing from '../../components/pricing';

import Hero from './Hero';
import Benefits from './Benefits';
import OnboardingSteps from './OnboardingSteps';

import { ReactComponent as PlatformAttributesDraw } from '../../assets/draws/ginastee-attributes.svg';
import FAQ from '../../components/faq';
import Demo from '../../components/demo';

import { ReactComponent as LinesEffect } from '../../assets/textures/lines.svg';
import Footer from '../../components/footer';

function AnimatedScrollSection({ containerRef, children }) {
    const sectionRef = useRef(null);
    const cacheRef = useRef({
        initialTop: null,
        height: null,
    });
    const lastDetailRef = useRef(null);
    const rafRef = useRef(null);
    
    // Estado para controlar se a animação deve ocorrer (apenas em telas > 720px)
    const [shouldAnimate, setShouldAnimate] = useState(() => window.innerWidth > 720);

    const recalcMetrics = useCallback((detail) => {
        const container = containerRef.current;
        const section = sectionRef.current;

        if (!container || !section) return;

        const scrollTop = detail?.scrollTop ?? container.scrollTop ?? 0;
        const elementRect = section.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        cacheRef.current.initialTop = elementRect.top - containerRect.top + scrollTop;
        cacheRef.current.height = section.offsetHeight;
    }, [containerRef]);

    const applyTransforms = useCallback((detail) => {
        const container = containerRef.current;
        const section = sectionRef.current;

        if (!detail || !container || !section) return;

        lastDetailRef.current = detail;

        // Se não deve animar (tela <= 720px), mantém valores iniciais e retorna
        if (!shouldAnimate) {
            const initialWidth = 98;
            const initialBorderRadius = 130;
            const nextWidth = `${initialWidth}%`;
            const nextBorderRadius = `${initialBorderRadius}px`;

            if (section.style.width !== nextWidth) {
                section.style.width = nextWidth;
            }

            if (section.style.borderRadius !== nextBorderRadius) {
                section.style.borderRadius = nextBorderRadius;
            }
            return;
        }

        if (cacheRef.current.initialTop === null || cacheRef.current.height === null) {
            recalcMetrics(detail);
        }

        const { initialTop, height } = cacheRef.current;

        if (initialTop === null || height === null) {
            return;
        }

        const scrollTop = detail.scrollTop;
        const viewportHeight = detail.clientHeight || container.clientHeight || window.innerHeight;

        const elementTopRelative = initialTop - scrollTop;
        const elementBottomRelative = initialTop + height - scrollTop;

        const visibleTop = Math.max(0, elementTopRelative);
        const visibleBottom = Math.min(viewportHeight, elementBottomRelative);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibilityPercentage = viewportHeight > 0 ? (visibleHeight / viewportHeight) * 100 : 0;

        const transitionStart = 15;
        const transitionEnd = 50;
        const initialWidth = 98;
        const finalWidth = 100;
        const initialBorderRadius = 130;
        const finalBorderRadius = 0;

        let width = initialWidth;
        let borderRadius = initialBorderRadius;

        if (visibilityPercentage < transitionStart) {
            width = initialWidth;
            borderRadius = initialBorderRadius;
        } else if (visibilityPercentage >= transitionEnd) {
            width = finalWidth;
            borderRadius = finalBorderRadius;
        } else {
            const progress = (visibilityPercentage - transitionStart) / (transitionEnd - transitionStart);
            width = initialWidth + (finalWidth - initialWidth) * progress;
            borderRadius = initialBorderRadius + (finalBorderRadius - initialBorderRadius) * progress;
        }

        const nextWidth = `${width}%`;
        const nextBorderRadius = `${borderRadius}px`;

        if (section.style.width !== nextWidth) {
            section.style.width = nextWidth;
        }

        if (section.style.borderRadius !== nextBorderRadius) {
            section.style.borderRadius = nextBorderRadius;
        }
    }, [containerRef, recalcMetrics, shouldAnimate]);

    useEffect(() => {
        const handlePageScroll = (event) => {
            const detail = event.detail;
            if (!detail) return;

            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }

            rafRef.current = requestAnimationFrame(() => {
                applyTransforms(detail);
                rafRef.current = null;
            });
        };

        const handleResize = () => {
            // Atualiza o estado de animação baseado no tamanho da tela
            setShouldAnimate(window.innerWidth > 720);
            
            cacheRef.current.initialTop = null;
            cacheRef.current.height = null;

            const latestDetail = window.__ginasteePageScrollState || lastDetailRef.current;
            if (latestDetail) {
                applyTransforms(latestDetail);
            }
        };

        window.addEventListener('page-scroll', handlePageScroll);
        window.addEventListener('resize', handleResize, { passive: true });

        const initialDetail = window.__ginasteePageScrollState;
        if (initialDetail) {
            applyTransforms(initialDetail);
        } else {
            // Se não deve animar, aplica valores iniciais diretamente
            if (!shouldAnimate && sectionRef.current) {
                sectionRef.current.style.width = '98%';
                sectionRef.current.style.borderRadius = '130px';
            }
            recalcMetrics();
        }

        return () => {
            window.removeEventListener('page-scroll', handlePageScroll);
            window.removeEventListener('resize', handleResize);

            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [applyTransforms, recalcMetrics, shouldAnimate]);

    return (
        <div
            ref={sectionRef}
            className="container-dark pb-6 mt-8"
            style={{
                width: '98%',
                borderRadius: '130px',
                marginBottom: '292px',
            }}
        >
            {children}
        </div>
    );
}

export default function HomePage() {
    const containerRef = useRef(null);
    const rafIdRef = useRef(null);
    const lastScrollTopRef = useRef(0);

    const dispatchScrollEvent = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            rafIdRef.current = null;
            return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight || window.innerHeight;
        const direction = scrollTop > lastScrollTopRef.current ? 'down' : 'up';

        lastScrollTopRef.current = scrollTop;

        const detail = {
            scrollTop,
            scrollHeight,
            clientHeight,
            maxScroll: Math.max(0, scrollHeight - clientHeight),
            direction,
        };

        window.__ginasteePageScrollState = detail;
        window.dispatchEvent(new CustomEvent('page-scroll', { detail }));

        rafIdRef.current = null;
    }, []);

    const handleScroll = useCallback(() => {
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(dispatchScrollEvent);
        }
    }, [dispatchScrollEvent]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        container.addEventListener('scroll', handleScroll, { passive: true });

        const handleResize = () => {
            if (rafIdRef.current === null) {
                rafIdRef.current = requestAnimationFrame(dispatchScrollEvent);
            }
        };

        window.addEventListener('resize', handleResize, { passive: true });

        const initTimeout = setTimeout(() => {
            dispatchScrollEvent();
        }, 0);

        return () => {
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            clearTimeout(initTimeout);

            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [dispatchScrollEvent, handleScroll]);

    return (
        <div className="home-page" ref={containerRef}>
            <Header containerRef={containerRef} />

            <Hero />

            <div className="mt-7 pt-6 platform-benefits-headline-section">
                <PlatformAttributesDraw className="platform-attributes-draw mt-9" />

                <div className="info-block mt-3">
                    <h1 className="mb-1">A única plataforma necessária para <h1 style={{ display: 'inline-block', textShadow: 'none' }} className="text-linear-animated">você crescer</h1> no nicho fitness</h1>
                    <h4>Criada especialmente para profissionais da saúde transformarem audiência em receita</h4>
                </div>
            </div>


            <AnimatedScrollSection containerRef={containerRef}>
                <div id="benefits-section">
                    <Benefits containerRef={containerRef} />
                </div>

                <TextAnimation />

                <OnboardingSteps />
            </AnimatedScrollSection>

            <div id="pricing-section">
                <Pricing />
            </div>


            <div id="faq-section" className="container-dark footer-container pb-6 mt-8 pt-8" style={{ width: '100%', borderRadius: '0px' }}>
                <FAQ style={{ zIndex: 1 }} />
                <Footer style={{ zIndex: 1 }} className='mt-8' />
                <LinesEffect className='lines-effect-footer' />
            </div>
        </div>
    );
}


function TextAnimation(){
    return(
        <div>
            
        </div>
    )
}